import {
  ORDER_TYPE_IDS,
  VISIT_TYPE_DISCIPLINE_MAP,
} from '../../constants/visitTypes';
import type {
  TaskListResponse,
  VisitTypeId,
  VisitTypeOption,
} from '../../services/api/types.gen';

export type TaskFilterFamily = 'all' | 'order' | 'visit';
export type TaskFilterVisitKind = 'all' | 'oasis' | 'non_oasis';
export type TaskFilterVisitDiscipline = 'all' | 'sn' | 'therapy';
export type TherapyDiscipline = 'PT' | 'OT' | 'ST';

export type TaskTypeFilterValue = {
  family: TaskFilterFamily;
  visitKind: TaskFilterVisitKind;
  discipline: TaskFilterVisitDiscipline;
  therapyDisciplines: ReadonlyArray<TherapyDiscipline>;
};

export type TaskTypeClassification = {
  family: TaskFilterFamily | 'unknown';
  visitKind: TaskFilterVisitKind;
  discipline: TaskFilterVisitDiscipline;
  therapyDiscipline: TherapyDiscipline | null;
  aliases: string[];
};

const OASIS_SUFFIX_ALIAS: Record<string, string> = {
  START_OF_CARE: 'SOC',
  RESUMPTION_OF_CARE: 'ROC',
  RECERTIFICATION: 'REC',
  DISCHARGE: 'DC',
};

const EMPTY_THERAPY_DISCIPLINES: ReadonlyArray<TherapyDiscipline> =
  Object.freeze([] as TherapyDiscipline[]);

function freezeTaskTypeFilter(
  filter: TaskTypeFilterValue
): TaskTypeFilterValue {
  return Object.freeze({
    ...filter,
    therapyDisciplines: Object.freeze([...filter.therapyDisciplines]),
  });
}

export const DEFAULT_TASK_TYPE_FILTER: TaskTypeFilterValue =
  freezeTaskTypeFilter({
    family: 'all',
    visitKind: 'all',
    discipline: 'all',
    therapyDisciplines: EMPTY_THERAPY_DISCIPLINES,
  });

export function createDefaultTaskTypeFilter(): TaskTypeFilterValue {
  return freezeTaskTypeFilter({
    family: 'all',
    visitKind: 'all',
    discipline: 'all',
    therapyDisciplines: [],
  });
}

export function normalizeTaskTypeFilter(
  filter: TaskTypeFilterValue
): TaskTypeFilterValue {
  if (filter.family !== 'visit') {
    return freezeTaskTypeFilter({
      ...filter,
      visitKind: 'all',
      discipline: 'all',
      therapyDisciplines: [],
    });
  }

  if (filter.discipline === 'sn') {
    return freezeTaskTypeFilter({
      ...filter,
      discipline: 'sn',
      therapyDisciplines: [],
    });
  }

  return freezeTaskTypeFilter({
    ...filter,
    therapyDisciplines:
      filter.discipline === 'therapy' ? filter.therapyDisciplines : [],
  });
}

export function setTaskTypeTopLevel(
  filter: TaskTypeFilterValue,
  family: TaskFilterFamily
): TaskTypeFilterValue {
  return normalizeTaskTypeFilter(setTaskFilterFamily(filter, family));
}

export function setTaskTypeVisitSubtype(
  filter: TaskTypeFilterValue,
  visitKind: TaskFilterVisitKind
): TaskTypeFilterValue {
  return normalizeTaskTypeFilter(setTaskFilterVisitKind(filter, visitKind));
}

export function setTaskTypeVisitDiscipline(
  filter: TaskTypeFilterValue,
  visitDiscipline: TaskFilterVisitDiscipline
): TaskTypeFilterValue {
  return normalizeTaskTypeFilter(
    setTaskFilterVisitDiscipline(filter, visitDiscipline)
  );
}

export function toggleTaskTypeTherapy(
  filter: TaskTypeFilterValue,
  therapyDiscipline: TherapyDiscipline
): TaskTypeFilterValue {
  return normalizeTaskTypeFilter(
    toggleTaskFilterTherapyDiscipline(filter, therapyDiscipline)
  );
}

export function isTaskTypeFilterDefault(filter: TaskTypeFilterValue): boolean {
  return !hasActiveTaskTypeFilter(filter);
}

export function getTaskTypeFilterSummary(filter: TaskTypeFilterValue): string {
  const normalized = normalizeTaskTypeFilter(filter);

  if (!hasActiveNormalizedTaskTypeFilter(normalized)) {
    return 'All task types';
  }

  const labels: string[] = [];

  if (normalized.family === 'order') {
    labels.push('Order');
  } else if (normalized.family === 'visit') {
    labels.push('Visit');

    if (normalized.visitKind === 'oasis') {
      labels.push('OASIS');
    } else if (normalized.visitKind === 'non_oasis') {
      labels.push('Non-OASIS');
    }

    if (normalized.discipline === 'sn') {
      labels.push('SN');
    } else if (normalized.discipline === 'therapy') {
      labels.push('Therapy');

      const orderedTherapies: TherapyDiscipline[] = ['PT', 'OT', 'ST'];
      const activeTherapies = orderedTherapies.filter(therapy =>
        normalized.therapyDisciplines.includes(therapy)
      );

      labels.push(...activeTherapies);
    }
  }

  return labels.join(' + ');
}

function hasActiveNormalizedTaskTypeFilter(
  normalized: TaskTypeFilterValue
): boolean {
  return (
    normalized.family !== 'all' ||
    normalized.visitKind !== 'all' ||
    normalized.discipline !== 'all' ||
    normalized.therapyDisciplines.length > 0
  );
}

export function hasActiveTaskTypeFilter(filter: TaskTypeFilterValue): boolean {
  return hasActiveNormalizedTaskTypeFilter(normalizeTaskTypeFilter(filter));
}

function setTaskFilterFamily(
  filter: TaskTypeFilterValue,
  family: TaskFilterFamily
): TaskTypeFilterValue {
  if (family === 'all') {
    return createDefaultTaskTypeFilter();
  }

  if (family === 'order') {
    return freezeTaskTypeFilter({
      ...filter,
      family,
      visitKind: 'all',
      discipline: 'all',
      therapyDisciplines: [],
    });
  }

  if (filter.family === 'visit') {
    return freezeTaskTypeFilter({ ...filter, family });
  }

  return freezeTaskTypeFilter({
    ...filter,
    family,
    visitKind: 'all',
    discipline: 'all',
    therapyDisciplines: [],
  });
}

function setTaskFilterVisitKind(
  filter: TaskTypeFilterValue,
  visitKind: TaskFilterVisitKind
): TaskTypeFilterValue {
  return freezeTaskTypeFilter({
    ...filter,
    family: 'visit',
    visitKind,
  });
}

function setTaskFilterVisitDiscipline(
  filter: TaskTypeFilterValue,
  visitDiscipline: TaskFilterVisitDiscipline
): TaskTypeFilterValue {
  return freezeTaskTypeFilter({
    ...filter,
    family: 'visit',
    discipline: visitDiscipline,
    therapyDisciplines:
      visitDiscipline === 'therapy' ? filter.therapyDisciplines : [],
  });
}

function toggleTaskFilterTherapyDiscipline(
  filter: TaskTypeFilterValue,
  therapyDiscipline: TherapyDiscipline
): TaskTypeFilterValue {
  const nextDisciplines = filter.therapyDisciplines.includes(therapyDiscipline)
    ? filter.therapyDisciplines.filter(value => value !== therapyDiscipline)
    : [...filter.therapyDisciplines, therapyDiscipline];

  return freezeTaskTypeFilter({
    ...filter,
    family: 'visit',
    visitKind: filter.visitKind,
    discipline: 'therapy',
    therapyDisciplines: nextDisciplines,
  });
}

function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getSearchableTokens(value: string): string[] {
  const normalized = normalizeText(value);
  return normalized ? normalized.split(' ').filter(Boolean) : [];
}

function isOrderTaskType(typeId: string): typeId is VisitTypeId {
  return ORDER_TYPE_IDS.has(typeId as VisitTypeId);
}

function visitKindForType(typeId: VisitTypeId): TaskFilterVisitKind {
  return typeId.startsWith('OASIS_') ? 'oasis' : 'non_oasis';
}

function visitDisciplineForType(
  typeId: VisitTypeId
): TaskFilterVisitDiscipline {
  const discipline = VISIT_TYPE_DISCIPLINE_MAP[typeId];
  return discipline === 'SN' ? 'sn' : 'therapy';
}

function therapyDisciplineForType(
  typeId: VisitTypeId
): TherapyDiscipline | null {
  const discipline = VISIT_TYPE_DISCIPLINE_MAP[typeId];
  return discipline === 'PT' || discipline === 'OT' || discipline === 'ST'
    ? discipline
    : null;
}

function buildVisitAliases(
  typeId: VisitTypeId,
  visitDiscipline: TaskFilterVisitDiscipline
): string[] {
  const aliases: string[] = [typeId];

  if (typeId.startsWith('OASIS_')) {
    aliases.push('OASIS');
    const suffix = typeId.replace(/^OASIS_E2_/, '');
    const normalizedSuffix = suffix.replace(/_(PT|OT|ST)$/, '');
    if (OASIS_SUFFIX_ALIAS[normalizedSuffix]) {
      aliases.push(OASIS_SUFFIX_ALIAS[normalizedSuffix]);
    }
  }

  if (visitDiscipline === 'sn') {
    aliases.push('SN');
  }

  if (visitDiscipline === 'therapy') {
    aliases.push('THERAPY');
    const therapyDiscipline = therapyDisciplineForType(typeId);
    if (therapyDiscipline !== null) {
      aliases.push(therapyDiscipline);
    }
  }

  return aliases;
}

function buildOrderAliases(typeId: VisitTypeId): string[] {
  const aliases: string[] = [typeId, 'ORDER'];
  if (typeId.includes('485')) {
    aliases.push('485');
  }
  return aliases;
}

function isKnownVisitType(typeId: string): typeId is VisitTypeId {
  return (
    !isOrderTaskType(typeId) &&
    Object.prototype.hasOwnProperty.call(VISIT_TYPE_DISCIPLINE_MAP, typeId)
  );
}

export function getTaskTypeClassification(
  typeId: string
): TaskTypeClassification {
  if (isOrderTaskType(typeId)) {
    return {
      family: 'order',
      visitKind: 'all',
      discipline: 'all',
      therapyDiscipline: null,
      aliases: buildOrderAliases(typeId as VisitTypeId),
    };
  }

  if (isKnownVisitType(typeId)) {
    const visitTypeId = typeId as VisitTypeId;
    const discipline = visitDisciplineForType(visitTypeId);
    return {
      family: 'visit',
      visitKind: visitKindForType(visitTypeId),
      discipline,
      therapyDiscipline:
        discipline === 'therapy' ? therapyDisciplineForType(visitTypeId) : null,
      aliases: buildVisitAliases(visitTypeId, discipline),
    };
  }

  return {
    family: 'unknown',
    visitKind: 'all',
    discipline: 'all',
    therapyDiscipline: null,
    aliases: [typeId],
  };
}

function matchesSearch(
  value: string,
  search: string,
  aliases: string[]
): boolean {
  const searchTokens = getSearchableTokens(search);
  if (searchTokens.length === 0) {
    return true;
  }

  const candidates = [value, ...aliases].flatMap(candidate =>
    getSearchableTokens(candidate)
  );

  return searchTokens.every(token =>
    candidates.some(candidate => candidate.startsWith(token))
  );
}

function matchesClassification(
  classification: TaskTypeClassification,
  filter: TaskTypeFilterValue
): boolean {
  if (classification.family === 'unknown') {
    return filter.family === 'all';
  }

  if (filter.family === 'order' && classification.family !== 'order') {
    return false;
  }

  if (filter.family === 'visit' && classification.family !== 'visit') {
    return false;
  }

  if (filter.family === 'visit') {
    if (
      filter.visitKind !== 'all' &&
      classification.visitKind !== filter.visitKind
    ) {
      return false;
    }

    if (
      filter.discipline !== 'all' &&
      classification.discipline !== filter.discipline
    ) {
      return false;
    }

    if (
      filter.discipline === 'therapy' &&
      filter.therapyDisciplines.length > 0
    ) {
      if (classification.therapyDiscipline === null) {
        return false;
      }

      if (
        !filter.therapyDisciplines.includes(classification.therapyDiscipline)
      ) {
        return false;
      }
    }
  }

  return true;
}

function getTaskRowClassification(
  task: TaskListResponse
): TaskTypeClassification {
  if (task.task_type === 'order') {
    return {
      family: 'order',
      visitKind: 'all',
      discipline: 'all',
      therapyDiscipline: null,
      aliases: [task.task_type, 'ORDER'],
    };
  }

  if (task.visit_type) {
    return getTaskTypeClassification(task.visit_type);
  }

  if (task.task_type === 'visit') {
    return {
      family: 'visit',
      visitKind: 'all',
      discipline: 'all',
      therapyDiscipline: null,
      aliases: [task.task_type, 'VISIT'],
    };
  }

  return {
    family: 'unknown',
    visitKind: 'all',
    discipline: 'all',
    therapyDiscipline: null,
    aliases: [task.task_type],
  };
}

export function filterVisitTypeOptions(
  options: VisitTypeOption[],
  filter: TaskTypeFilterValue,
  searchQuery?: string
): VisitTypeOption[] {
  const normalizedFilter = normalizeTaskTypeFilter(filter);

  return options.filter(option => {
    const classification = getTaskTypeClassification(option.id);

    return (
      matchesClassification(classification, normalizedFilter) &&
      matchesSearch(
        `${option.id} ${option.display_name}`,
        searchQuery ?? '',
        classification.aliases
      )
    );
  });
}

export function filterTaskRows<T extends TaskListResponse>(
  rows: T[],
  filter: TaskTypeFilterValue
): T[] {
  const normalizedFilter = normalizeTaskTypeFilter(filter);

  return rows.filter(row =>
    matchesClassification(getTaskRowClassification(row), normalizedFilter)
  );
}
